import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Dropdown, type DropdownProps } from 'semantic-ui-react';
import { useToasts } from 'common/toast';
import { useCultureHubQueryParams } from '@/areas/culture-hub/hooks/useCultureHubQueryParams';
import { useGetActionPlansQuery } from '@/areas/culture-hub/api/cultureHubApi';
import { useApiPortalV1AssessmentsPlansInterventionsDestroyMutation } from '@/store/generated/portal/assessments/plans-interventions';
import { useInterventionsPlanSync } from '@/areas/culture-hub/hooks/useInterventionsPlanSync';
import { getAssessmentPeriods, getCurrentOrganizationId, getLeadershipTeamsForSelectedAssessmentPeriod, getSelectedAssessmentPeriodId, isCurrentMemberTeamLeader } from '@/common/selectors/auth';
import userContextSlice from '@/common/slices/userContextSlice';
import { TopBarContext } from '@/common/layouts/AuthLayout/TopBarContext';
import { StyledAddButton } from '@/common/components/button/StyledButton';
import {
  getPlan,
  removeRecommendationFromPlan,
} from '@/areas/culture-hub/slices/cultureHubInterventionsSlice';
import MobilePlannerTable from './action-planner/MobilePlannerTable';
import MobileTopBarSelectors from '@/mobile/components/MobileTopBarSelectors';
import MobileAssessmentPeriodList from '@/mobile/areas/dashboard/MobileAssessmentPeriodList';
import { MobilePageContent, MobilePageSection, Title, SectionHeader, SectionDescription, MobileSpinner } from '@/mobile/layouts/common/MobilePageStyles';

const MobileFocusAreasTab: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const { assessmentPeriodId, teamId, organizationId, shouldFetchData, isMostRecentClosedAssessmentPeriod } = useCultureHubQueryParams();
  const isTeamLeader = useSelector(isCurrentMemberTeamLeader);
  const plan = useSelector(getPlan);
  const shouldFetchActionPlans = !!assessmentPeriodId && !!shouldFetchData && !!organizationId && !!teamId;

  const { data: actionPlansData, isLoading: actionPlansLoading, isFetching: actionPlansFetching, refetch: refetchActionPlans } = useGetActionPlansQuery(
    { organizationId: organizationId!, assessmentPeriodId: assessmentPeriodId!, teamId },
    {
      skip: !shouldFetchActionPlans,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
    }
  );

  const actionPlans = actionPlansData ?? [];
  const existingActionPlans = actionPlans;

  useInterventionsPlanSync(existingActionPlans, assessmentPeriodId, teamId);

  const [deletePlan] = useApiPortalV1AssessmentsPlansInterventionsDestroyMutation();

  const handleRemove = async (identifier: string | number | object) => {
    const matchedItem = (() => {
      if (typeof identifier === 'object' && identifier !== null) {
        const rec = identifier as any;
        return plan.find((p: any) => p.recommendation_template_id === rec.recommendation_template_id && (p as any).task_index === (rec.task_index ?? 0));
      }
      return plan.find((p: any) => (p as any).task_id === identifier || p.recommendation_template_id === identifier || (p as any).action_plan_id === identifier);
    })();

    if (matchedItem && (matchedItem as any).action_plan_id) {
      try {
        await deletePlan({ id: (matchedItem as any).action_plan_id }).unwrap();
        addToast('Focus area removed', { appearance: 'success', autoDismiss: true });
        await refetchActionPlans();
      } catch (err: any) {
        console.error('Failed to delete action plan record:', err);
        if (err && err.status === 405) {
          addToast('Failed to remove focus area: server does not support deletion (405).', { appearance: 'error', autoDismiss: true });
        } else {
          addToast('Failed to remove focus area. Please try again.', { appearance: 'error', autoDismiss: true });
        }
        return;
      }
    }

    dispatch(removeRecommendationFromPlan(identifier));

    if (!(matchedItem && (matchedItem as any).action_plan_id)) {
      addToast('Focus area removed', { appearance: 'success', autoDismiss: true });
    }
  };

  const assessmentPeriods = useSelector(getAssessmentPeriods);
  const selectedTeamId = useSelector(userContextSlice.selectors.getSelectedTeamId as any) as number | undefined;
  const selectedAssessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const currentOrganizationId = useSelector(getCurrentOrganizationId);
  const leadershipTeams = useSelector(getLeadershipTeamsForSelectedAssessmentPeriod);
  const teamName = leadershipTeams?.find(team => team.id === teamId)?.name;
  const { setTopBarContent } = React.useContext(TopBarContext);

  React.useEffect(() => {
    if ((selectedTeamId === undefined || selectedTeamId === null) && teamId != null) {
      dispatch(userContextSlice.actions.setSelectedTeamId(teamId));
    }
  }, [dispatch, selectedTeamId, teamId]);

  const handleTeamChange = React.useCallback(
    (value: string | number | undefined) => {
      const nextTeamId = typeof value === 'string' ? Number(value) : value;
      if (nextTeamId && nextTeamId !== selectedTeamId) {
        dispatch(userContextSlice.actions.setSelectedTeamId(nextTeamId));
      }
    },
    [dispatch, selectedTeamId]
  );

  const handleAssessmentPeriodChange = React.useCallback(
    (value: string | number | undefined, period?: any) => {
      const selected = period ?? assessmentPeriods?.find((p: any) => p.id === value);
      if (selected && selectedAssessmentPeriodId !== selected.id) {
        dispatch(
          userContextSlice.actions.setSelectedAssessmentPeriod({
            selectedAssessmentPeriod: selected,
            assessmentPeriods,
          })
        );
      }
    },
    [assessmentPeriods, dispatch, selectedAssessmentPeriodId]
  );

  React.useEffect(() => {
    setTopBarContent(
      <MobileTopBarSelectors
        teamDropdown={
          leadershipTeams?.length ? (
            <Dropdown
              className="mobile-control-dropdown"
              placeholder="Select a team"
              fluid
              selection
              options={leadershipTeams.map(team => ({
                key: team.id,
                value: team.id,
                text: team.name,
              }))}
              value={teamId ?? undefined}
              onChange={(_event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
                const nextTeamId = data.value as string | number | undefined;
                handleTeamChange(nextTeamId);
              }}
              style={{
                width: '100%',
                border: 'none',
                boxShadow: 'none',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '18px',
                fontWeight: 550,
                minHeight: '40px',
                lineHeight: '20px',
                paddingLeft: '10px',
                paddingTop: '6px',
                paddingBottom: '6px',
              }}
            />
          ) : undefined
        }
        periodDropdown={
          <MobileAssessmentPeriodList
            className="mobile-control-dropdown"
            currentOrganizationId={currentOrganizationId}
            selectedAssessmentPeriodId={selectedAssessmentPeriodId}
            onChange={handleAssessmentPeriodChange}
          />
        }
      />
    );

    return () => setTopBarContent(null);
  }, [
    assessmentPeriods,
    currentOrganizationId,
    handleAssessmentPeriodChange,
    handleTeamChange,
    leadershipTeams,
    selectedAssessmentPeriodId,
    selectedTeamId,
    setTopBarContent,
    teamId,
  ]);

  const getPlanItemTeamId = (item: any): number | undefined => {
    if (typeof item.team_id === 'number') return item.team_id;
    if (typeof item.team_id === 'string' && /^\d+$/.test(item.team_id)) return Number(item.team_id);
    if (item.team && typeof item.team === 'object' && typeof item.team.id === 'number') return item.team.id;
    if (item.team && typeof item.team === 'object' && typeof item.team.id === 'string' && /^\d+$/.test(item.team.id)) return Number(item.team.id);
    return undefined;
  };

  const planForTeam = teamId == null ? plan : plan.filter((item: any) => getPlanItemTeamId(item) === teamId);
  const hasFetchStarted = shouldFetchActionPlans;
  const isLoading = actionPlansLoading || actionPlansFetching;
  const showSpinner = !hasFetchStarted || isLoading;

  return (
    <MobilePageContent>
      <MobilePageSection>
        <SectionHeader>
          <Title>{`Focus Plan${teamName ? ` for ${teamName}` : ''}`}</Title>
        </SectionHeader>
        <SectionDescription>
          Track your team's current focus areas and manage your plan.
        </SectionDescription>
      </MobilePageSection>

      <MobilePageSection padtop>
        {showSpinner ? (
          <MobileSpinner text="Loading focus areas..." />
        ) : (
          <MobilePlannerTable
            items={planForTeam}
            isLoading={isLoading}
            onRemove={handleRemove}
            isMostRecent={isMostRecentClosedAssessmentPeriod}
          />
        )}
      </MobilePageSection>

      <MobilePageSection>
        <BottomActions>
          <StyledAddButton
            label="Add to Focus Plan"
            width="100%"
            height="46px"
            disabled={!isMostRecentClosedAssessmentPeriod}
            onClick={() => navigate('/app/mobile/focus-areas/recommendations')}
          />
        </BottomActions>
      </MobilePageSection>
    </MobilePageContent>
  );
};

export default MobileFocusAreasTab;

const BottomActions = styled.div`
  margin-top: 14px;
`;
